import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, Loader, Space, Table, useNotify } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { ExportsFilter } from 'features/documents/Filters/ExportsFilter';
import { useQueryParams } from 'hooks/useQueryParams';
import { useRolePermission } from 'hooks/useRolePermission';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';
import { BlobResponse } from 'hooks/useFetcher/useFetcher';
import { useExportsAPI, LEAD_EXPORT_TYPE } from 'api';
import { Column, ExportJob, ExportStatus, Results } from 'types';
import { dateTimeFormat, format } from 'libs';
import { defaultFilters } from 'utils';

const isRunning = (status: ExportStatus): boolean =>
  status === ExportStatus.Pending || status === ExportStatus.Processing;

export const ContactsExport: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const { getExports, downloadExport } = useExportsAPI();

  const hasPermission = useRolePermission([
    UserRoleCRM.regionalManager,
    UserRoleCRM.admin,
    UserRoleCRM.marketing,
  ]);

  const { data: exports, isLoading } = useQuery<Results<ExportJob> | ExportJob[]>(
    ['exports', { ...defaultFilters, ...params, type: LEAD_EXPORT_TYPE }],
    getExports,
    {
      enabled: hasPermission,
      onError: () => notify.error({ title: t('error.someThingIsWrong') }),
      // Poll while any job is still running so finished files appear on their own.
      refetchInterval: (data) => {
        const list = Array.isArray(data) ? data : data?.results ?? [];
        return list.some((job) => isRunning(job.status)) ? 5000 : false;
      },
    },
  );

  // The endpoint may return either a paginated wrapper or a bare array.
  const rows = Array.isArray(exports) ? exports : exports?.results ?? [];
  const count = Array.isArray(exports) ? exports.length : exports?.count ?? 0;
  const totalPages = Array.isArray(exports) ? 0 : exports?.total_pages ?? 0;

  const downloadMutation = useMutation((id: number) => downloadExport(id), {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: ({ blob, filename }: BlobResponse, id) => {
      if (!blob) {
        notify.error({ title: t('error.someThingIsWrong') });
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename || `export-${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });

  const columns: Column<ExportJob>[] = React.useMemo(
    () => [
      {
        title: t('exports.name'),
        dataIndex: 'name',
      },
      {
        title: t('exports.status'),
        render: ({ status, error_message }): React.ReactNode => (
          <div className="medium" title={status === ExportStatus.Failed ? error_message || '' : ''}>
            {t(`exports.status_${status}`)}
          </div>
        ),
      },
      {
        title: t('exports.createdAt'),
        render: ({ created_at }): React.ReactNode => (
          <div className="medium">{created_at ? format(created_at, dateTimeFormat) : '---'}</div>
        ),
      },
      {
        title: t('exports.finishedAt'),
        render: ({ finished_at }): React.ReactNode => (
          <div className="medium">{finished_at ? format(finished_at, dateTimeFormat) : '---'}</div>
        ),
      },
      {
        title: t('exports.action'),
        align: 'right',
        render: ({ id, status }): React.ReactNode => (
          <Space justify="end">
            <Button
              size="small"
              disabled={status !== ExportStatus.Finished}
              loading={downloadMutation.isLoading && downloadMutation.variables === id}
              onClick={() => downloadMutation.mutate(id)}
            >
              {t('exports.download')}
            </Button>
          </Space>
        ),
      },
    ],
    [t, downloadMutation.isLoading, downloadMutation.variables],
  );

  if (!hasPermission) {
    return <Redirect to="/documents" />;
  }

  return (
    <>
      <ExportsFilter count={count} />
      <Card>
        <Loader loading={isLoading} size="regular">
          <Table emptyText={t('main.noData')} data={rows} columns={columns} className="no-border" />
        </Loader>
        <Card.Footer>
          <Pagination size={totalPages} />
        </Card.Footer>
      </Card>
    </>
  );
};
